import {LitElement, html, css} from 'lit';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import {jsPDF} from 'jspdf';
import {morphedRead} from '../../auth/morphed-client.js';
import {clamBackend, type InvitationRow, type InvitationExportRow, type EventTemplateListItem, type SendResult} from '../../services/clam-backend.service.js';
import {iconPencil, iconMail, iconCheck, iconX, iconWarning, iconDownload, iconCopy} from '../../utils/icons.js';
import '../clam-step-progress/clam-step-progress.js';
import type {StepItem, StepChunkResult} from '../clam-step-progress/clam-step-progress.js';

interface Evento {
	id: string;
	eventName: string;
	modalidad?: string;
}

export class ClamQrManager extends LitElement {
	static styles = css`
		:host { display: block; padding: var(--clam-space-6); max-width: 1100px; margin: 0 auto; font-family: var(--clam-font); color: var(--clam-text); }
		h2 { color: var(--clam-text); margin: 0 0 var(--clam-space-2); font-size: var(--clam-text-2xl); }
		p.desc { color: var(--clam-text-muted); font-size: var(--clam-text-base); margin: 0 0 var(--clam-space-6); }
		.toolbar { display: flex; gap: var(--clam-space-3); align-items: center; flex-wrap: wrap; margin-bottom: var(--clam-space-5); }
		select { padding: 10px 12px; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); font-family: inherit; min-width: 0; width: 100%; max-width: 360px; box-sizing: border-box; color: var(--clam-text); background: var(--clam-surface); }
		.tpl-bar { display: flex; gap: var(--clam-space-3); align-items: center; flex-wrap: wrap; margin-bottom: var(--clam-space-5); padding: var(--clam-space-3) var(--clam-space-4); background: var(--clam-surface-soft); border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); }
		.tpl-bar label { font-size: var(--clam-text-sm); font-weight: 600; color: var(--clam-text); white-space: nowrap; }
		.tpl-bar select { flex: 1; min-width: 180px; max-width: 360px; }
		.tpl-link { background: none; border: none; color: var(--clam-primary); font-family: inherit; font-size: var(--clam-text-sm); font-weight: 600; cursor: pointer; text-decoration: underline; padding: 8px 4px; min-height: 40px; display: inline-flex; align-items: center; }
		.tpl-link:hover { color: var(--clam-primary-hover); }
		.tpl-hint { font-size: var(--clam-text-xs); color: var(--clam-text-muted); width: 100%; margin-top: 2px; }
		.stats { display: flex; gap: var(--clam-space-3); margin-bottom: var(--clam-space-5); flex-wrap: wrap; }
		.stat { background: var(--clam-surface-soft); border-radius: var(--clam-radius-md); padding: var(--clam-space-3) var(--clam-space-5); text-align: center; flex: 1 1 90px; min-width: 90px; }
		.stat-num { font-size: var(--clam-text-2xl); font-weight: 700; color: var(--clam-primary); }
		.stat-label { font-size: var(--clam-text-xs); color: var(--clam-text-muted); text-transform: uppercase; }
		.stat-env .stat-num { color: var(--clam-green-dark); }
		.stat-conf .stat-num { color: var(--clam-success); }
		.btn { padding: 10px var(--clam-space-5); border: none; border-radius: var(--clam-radius-md); font-size: var(--clam-text-sm); font-weight: 600; cursor: pointer; font-family: inherit; min-height: 40px; }
		.btn-primary { background: var(--clam-primary); color: var(--clam-on-primary); }
		.btn-primary:hover { background: var(--clam-primary-hover); }
		.btn-send { background: var(--clam-green-dark); color: var(--clam-on-primary); }
		.btn-secondary { background: var(--clam-surface-soft); color: var(--clam-text); border: 1px solid var(--clam-border); }
		.btn-secondary:hover { background: var(--clam-border); }
		.btn:disabled { opacity: 0.5; cursor: not-allowed; }
		.table-wrap { width: 100%; }
		.table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); }
		table { width: 100%; border-collapse: collapse; font-size: var(--clam-text-sm); min-width: 720px; }
		th { text-align: left; padding: 10px 8px; border-bottom: 2px solid var(--clam-border); color: var(--clam-text); font-size: var(--clam-text-xs); text-transform: uppercase; background: var(--clam-surface-soft); }
		td { padding: 10px 8px; border-bottom: 1px solid var(--clam-border); }
		tr:hover td { background: var(--clam-surface-soft); }
		.check { width: 20px; height: 20px; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; font-size: var(--clam-text-sm); }
		.check svg { width: 14px; height: 14px; }
		.check-yes { background: #dcfce7; color: var(--clam-success); }
		.check-no { background: #fee2e2; color: var(--clam-danger, #ef4444); }
		.badge { font-size: var(--clam-text-xs); padding: 2px 8px; border-radius: 10px; font-weight: 600; }
		.badge-env { background: #dcfce7; color: var(--clam-green-dark); }
		.badge-pend { background: #fef3c7; color: #d97706; }
		.mod-badge { font-size: var(--clam-text-sm); padding: 4px 12px; border-radius: 20px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
		.mod-presencial { background: #dcfce7; color: var(--clam-green-dark); }
		.mod-virtual { background: #dbeafe; color: var(--clam-green-dark); }
		.mod-hibrido { background: #fef3c7; color: #d97706; }
		.qr-preview { max-width: 80px; cursor: pointer; border-radius: 4px; border: 1px solid var(--clam-border); }
		.empty { text-align: center; padding: 40px; color: var(--clam-text-muted); }
		.msg { padding: var(--clam-space-3) var(--clam-space-4); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); margin-bottom: var(--clam-space-4); }
		.msg-ok { background: #e8f8e8; color: var(--clam-green-dark); }
		.msg-err { background: #fde8e8; color: var(--clam-danger); }
		.msg-info { background: #e8f4fd; color: var(--clam-green-dark); }
		.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: currentColor; border-radius: 50%; animation: spin 0.8s linear infinite; vertical-align: middle; }
		@keyframes spin { to { transform: rotate(360deg); } }
		.btn-icon .spinner, .inv-action .spinner { border-color: var(--clam-border); border-top-color: var(--clam-primary); }
		.btn-preview { background: var(--clam-mod-green); color: var(--clam-on-primary); }
		.btn-preview:hover { background: var(--clam-light-green); }
		.btn-icon { padding: 8px 12px; border: none; border-radius: var(--clam-radius-sm); cursor: pointer; font-size: var(--clam-text-sm); background: var(--clam-surface-soft); color: var(--clam-primary); min-height: 40px; }
		.btn-icon:hover { background: #e6f1d8; }
		.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }
		.progress-bar { width: 100%; height: 6px; background: var(--clam-border); border-radius: 3px; overflow: hidden; margin-bottom: var(--clam-space-3); }
		.progress-fill { height: 100%; background: var(--clam-primary); transition: width 0.3s; }
		.preview-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: var(--clam-space-4); }
		.preview-modal { background: var(--clam-surface); border-radius: var(--clam-radius-lg); max-width: 700px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
		.preview-header { display: flex; justify-content: space-between; align-items: center; padding: var(--clam-space-4) var(--clam-space-5); border-bottom: 1px solid var(--clam-border); flex-shrink: 0; }
		.preview-header h3 { margin: 0; font-size: var(--clam-text-lg); color: var(--clam-text); }
		.preview-close { background: none; border: none; font-size: 24px; cursor: pointer; color: var(--clam-text-muted); min-width: 40px; min-height: 40px; border-radius: var(--clam-radius-sm); display: flex; align-items: center; justify-content: center; }
		.preview-close:hover { background: var(--clam-surface-soft); color: var(--clam-text); }
		.preview-body { overflow-y: auto; flex: 1; padding: 0; }
		.preview-body iframe { width: 100%; border: none; display: block; }
		.preview-footer { display: flex; justify-content: space-between; align-items: center; gap: var(--clam-space-3); padding: var(--clam-space-3) var(--clam-space-5); border-top: 1px solid var(--clam-border); flex-shrink: 0; flex-wrap: wrap; background: var(--clam-surface-soft); }
		.preview-subject { font-size: var(--clam-text-sm); color: var(--clam-text-muted); min-width: 0; word-break: break-word; flex: 1; }
		.preview-actions { display: flex; gap: var(--clam-space-2); flex-shrink: 0; }
		.row-actions { display: inline-flex; gap: 6px; justify-content: center; }
		.inv-card .inv-action { display: flex; flex-direction: column; gap: 6px; }
		.confirm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1100; display: flex; align-items: center; justify-content: center; padding: var(--clam-space-4); }
		.confirm-modal { background: var(--clam-surface); border-radius: var(--clam-radius-lg); max-width: 440px; width: 100%; box-shadow: var(--clam-shadow-lg); overflow: hidden; }
		.confirm-modal .modal__header { padding: var(--clam-space-4) var(--clam-space-5); border-bottom: 1px solid var(--clam-border); display: flex; align-items: center; gap: var(--clam-space-3); }
		.confirm-modal .modal__header h3 { margin: 0; font-size: var(--clam-text-lg); }
		.confirm-modal .modal__header .ic { width: 32px; height: 32px; border-radius: 50%; background: #fef3c7; color: #d97706; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
		.confirm-modal .modal__body { padding: var(--clam-space-4) var(--clam-space-5); color: var(--clam-text); font-size: var(--clam-text-base); }
		.confirm-modal .modal__body strong { color: var(--clam-text); }
		.confirm-modal .modal__footer { padding: var(--clam-space-3) var(--clam-space-5); border-top: 1px solid var(--clam-border); display: flex; justify-content: flex-end; gap: var(--clam-space-2); }
		.inv-cards { display: flex; flex-direction: column; gap: var(--clam-space-3); }
		.inv-card { background: var(--clam-surface); border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); padding: var(--clam-space-4); display: flex; gap: var(--clam-space-4); align-items: center; box-shadow: var(--clam-shadow-sm); }
		.inv-card .inv-qr { width: 64px; height: 64px; object-fit: contain; border-radius: var(--clam-radius-sm); border: 1px solid var(--clam-border); flex-shrink: 0; background: var(--clam-surface-soft); }
		.inv-card .inv-info { flex: 1; min-width: 0; }
		.inv-card .inv-name { font-weight: 700; color: var(--clam-text); }
		.inv-card .inv-meta { color: var(--clam-text-muted); font-size: var(--clam-text-sm); word-break: break-word; }
		.inv-card .inv-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
		.inv-card .inv-action { flex-shrink: 0; }
		.inv-table { display: none; }
		@media (min-width: 768px) {
			.inv-table { display: block; }
			.inv-cards { display: none; }
		}
	`;

	private _eventos: Evento[] = [];
	private _selectedId = '';
	private _invitations: InvitationRow[] = [];
	private _templates: EventTemplateListItem[] = [];
	private _selectedTemplateId = '';
	private _loading = false;
	private _action: 'generate' | 'send' | 'preview' | null = null;
	private _loadingEvento = false;
	private _msg: {type: string; text: string} | null = null;
	private _previewHtml: string | null = null;
	private _sendingRow: string | null = null;
	private _confirmBatch: InvitationRow[] | null = null;
	private _progressItems: StepItem[] | null = null;
	private _exporting = false;
	private _personPreview: {row: InvitationRow; html: string; subject: string} | null = null;
	private _loadingPreview: string | null = null;
	private _copyState: 'idle' | 'ok' | 'err' = 'idle';
	private _pdfLoading = false;

	connectedCallback(): void {
		super.connectedCallback();
		void this._loadEventos();
	}

	private async _loadEventos(): Promise<void> {
		this._loading = true;
		try {
			this._eventos = await morphedRead<Evento>('Clam Evento', {options: {limit: 50}});
			if (this._eventos.length > 0) {
				this._selectedId = this._eventos[0].id;
				await this._loadTemplates();
				await this._loadInvitations();
			}
		} catch (e) {
			this._msg = {type: 'err', text: `Error: ${e instanceof Error ? e.message : String(e)}`};
		}
		this._loading = false;
		this.requestUpdate();
	}

	private async _loadTemplates(): Promise<void> {
		if (!this._selectedId) {
			this._templates = [];
			this._selectedTemplateId = '';
			return;
		}
		const res = await clamBackend.listEventTemplates(this._selectedId);
		if (res.ok && res.data) {
			this._templates = res.data.data;
			// Preselección: si el evento tiene plantilla(s), deja la primera (preserva conducta
			// anterior donde el envío usaba la plantilla del evento); si no, "email por defecto".
			this._selectedTemplateId = this._templates.length > 0 ? this._templates[0].id : '';
		} else {
			this._templates = [];
			this._selectedTemplateId = '';
		}
	}

	private async _loadInvitations(): Promise<void> {
		if (!this._selectedId) return;
		const res = await clamBackend.listInvitations(this._selectedId);
		if (res.ok && res.data) {
			this._invitations = res.data.data;
		} else {
			this._invitations = [];
			this._msg = {type: 'err', text: res.error || 'Error cargando invitaciones'};
		}
		this.requestUpdate();
	}

	private async _onSelect(e: Event): Promise<void> {
		this._selectedId = (e.target as HTMLSelectElement).value;
		this._msg = null;
		// Feedback inmediato: limpia la data del evento previo y muestra carga mientras carga el nuevo.
		this._loadingEvento = true;
		this._invitations = [];
		this._templates = [];
		this._selectedTemplateId = '';
		this.requestUpdate();
		try {
			await this._loadTemplates();
			await this._loadInvitations();
		} finally {
			this._loadingEvento = false;
			this.requestUpdate();
		}
	}

	private _onSelectTemplate(e: Event): void {
		this._selectedTemplateId = (e.target as HTMLSelectElement).value;
		this._msg = null;
		this.requestUpdate();
	}

	private _openTemplateEditor(): void {
		this.dispatchEvent(new CustomEvent('open-template-editor', {
			detail: {eventoId: this._selectedId},
			bubbles: true,
			composed: true,
		}));
	}

	private async _generate(): Promise<void> {
		this._action = 'generate';
		this._msg = {type: 'info', text: 'Generando invitaciones y códigos QR...'};
		this.requestUpdate();
		const res = await clamBackend.generateInvitations(this._selectedId);
		if (res.ok && res.data) {
			this._msg = {type: 'ok', text: `${res.data.generated} invitaciones generadas, ${res.data.skipped} ya existían.`};
			await this._loadInvitations();
		} else {
			this._msg = {type: 'err', text: res.error || 'Error generando invitaciones'};
		}
		this._action = null;
		this.requestUpdate();
	}

	private async _preview(): Promise<void> {
		this._action = 'preview';
		this._msg = {type: 'info', text: 'Cargando vista previa del email...'};
		this.requestUpdate();
		const res = await clamBackend.previewEmail(this._selectedId, this._selectedTemplateId || undefined);
		if (res.ok && res.data) {
			this._previewHtml = res.data.html;
			this._msg = null;
		} else {
			this._msg = {type: 'err', text: res.statusCode === 401 ? 'Tu sesión expiró. Recarga e inicia sesión de nuevo.' : (res.error || 'Error generando preview')};
		}
		this._action = null;
		this.requestUpdate();
	}

	private _closePreview(): void {
		this._previewHtml = null;
		this.requestUpdate();
	}

	private async _sendOne(invitationId: string): Promise<void> {
		this._sendingRow = invitationId;
		this.requestUpdate();
		const res = await clamBackend.sendOneInvitation(invitationId, this._selectedTemplateId || undefined);
		if (res.ok && res.data) {
			if (res.data.sent) {
				this._msg = {type: 'ok', text: `Correo enviado a ${res.data.email}`};
			} else {
				this._msg = {type: 'err', text: `Falló envío a ${res.data.email}: ${res.data.error || 'error'}`};
			}
		} else {
			this._msg = {type: 'err', text: res.error || 'Error enviando correo'};
		}
		this._sendingRow = null;
		await this._loadInvitations();
	}

	private async _sendBatch(): Promise<void> {
		const unsent = this._invitations.filter((i) => !i.enviado);
		if (unsent.length === 0) {
			this._msg = {type: 'info', text: 'No hay invitaciones pendientes de envío.'};
			this.requestUpdate();
			return;
		}
		this._confirmBatch = unsent;
		this.requestUpdate();
	}

	private _cancelConfirmSend(): void {
		this._confirmBatch = null;
		this.requestUpdate();
	}

	private _confirmSendBatch(): void {
		if (!this._confirmBatch) return;
		this._progressItems = this._confirmBatch.map((inv) => ({
			id: inv.id,
			label: inv.personaName || 'Sin nombre',
			sublabel: inv.personaEmail || 'sin correo',
		}));
		this._confirmBatch = null;
		this._action = 'send';
		this._msg = null;
		this.requestUpdate();
	}

	private async _onProgressDone(e: CustomEvent<{sent: number; failed: number; total: number; cancelled: boolean}>): Promise<void> {
		const {sent, failed, total, cancelled} = e.detail;
		if (cancelled) {
			this._msg = {type: 'info', text: `Envío cancelado: ${sent} correos enviados.`};
		} else if (failed === 0) {
			this._msg = {type: 'ok', text: `${sent} correos enviados correctamente.`};
		} else {
			this._msg = {type: 'err', text: `${sent}/${total} enviados, ${failed} fallaron. Revisa los detalles en la lista.`};
		}
		this.requestUpdate();
	}

	private async _onProgressClose(): Promise<void> {
		this._progressItems = null;
		this._action = null;
		await this._loadInvitations();
		this.requestUpdate();
	}

	private _stripHtml(html: string): string {
		const tmp = document.createElement('div');
		tmp.innerHTML = html;
		return (tmp.textContent || tmp.innerText || '').trim();
	}

	private async _export(): Promise<void> {
		if (this._exporting || !this._selectedId) return;
		this._exporting = true;
		this._msg = {type: 'info', text: 'Generando Excel...'};
		this.requestUpdate();
		const res = await clamBackend.exportInvitations(this._selectedId);
		if (!res.ok || !res.data) {
			this._msg = {type: 'err', text: res.error || 'Error exportando invitaciones'};
			this._exporting = false;
			this.requestUpdate();
			return;
		}
		const rows = res.data.data.map((r: InvitationExportRow) => ({
			'Nombre': r.name,
			'Email': r.email,
			'Teléfono': r.phone,
			'Empresa': r.empresa,
			'Rol': r.rol,
			'Puesto': r.puesto,
			'Tipo Organización': r.tipoOrganizacion,
			'Estado': r.estado,
			'Municipio': r.municipio,
			'Miembro CLAM': r.esMiembroClam ? 'Sí' : 'No',
			'Alergias': r.alergias,
			'Email Institucional': r.emailInstitucional,
			'Gafete': r.gafete,
			'Descripción': r.description,
			'Enviado': r.enviado ? 'Sí' : 'No',
			'Envíos': r.enviosCount,
			'Último Envío': r.ultimoEnvio ? new Date(r.ultimoEnvio).toLocaleString('es-MX') : '',
			'Confirmado': r.confirmado ? 'Sí' : 'No',
			'Confirmado En': r.confirmadoAt ? new Date(r.confirmadoAt).toLocaleString('es-MX') : '',
			'Token QR': r.qrToken,
			'ID Persona': r.id,
			'ID Invitación': r.invitationId,
		}));
		const ws = XLSX.utils.json_to_sheet(rows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Invitaciones');
		const eventName = this._eventos.find((e) => e.id === this._selectedId)?.eventName || 'evento';
		const safeName = eventName.replace(/[^\w\s.-]/g, '').trim().slice(0, 50) || 'evento';
		XLSX.writeFile(wb, `Invitaciones - ${safeName}.xlsx`);
		this._exporting = false;
		this._msg = {type: 'ok', text: `Excel generado con ${rows.length} filas.`};
		this.requestUpdate();
	}

	private async _openPersonPreview(inv: InvitationRow): Promise<void> {
		if (this._loadingPreview) return;
		this._loadingPreview = inv.id;
		this._copyState = 'idle';
		this._msg = null;
		this.requestUpdate();
		const res = await clamBackend.previewInvitationEmail(inv.id, this._selectedTemplateId || undefined);
		this._loadingPreview = null;
		if (res.ok && res.data) {
			this._personPreview = {row: inv, html: res.data.html, subject: res.data.subject};
		} else {
			this._msg = {type: 'err', text: res.statusCode === 401 ? 'Tu sesión expiró. Recarga e inicia sesión.' : (res.error || 'Error generando preview')};
		}
		this.requestUpdate();
	}

	private _closePersonPreview(): void {
		this._personPreview = null;
		this._copyState = 'idle';
		this.requestUpdate();
	}

	private async _copyPreviewHtml(): Promise<void> {
		const html = this._personPreview?.html;
		if (!html) return;
		const text = this._stripHtml(html);
		try {
			const clip = navigator.clipboard;
			const ClipboardItemCtor = (window as unknown as {ClipboardItem?: typeof ClipboardItem}).ClipboardItem;
			if (clip && ClipboardItemCtor) {
				const item = new ClipboardItemCtor({
					'text/html': new Blob([html], {type: 'text/html'}),
					'text/plain': new Blob([text], {type: 'text/plain'}),
				});
				await clip.write([item]);
				this._copyState = 'ok';
			} else if (clip) {
				await clip.writeText(text);
				this._copyState = 'ok';
			} else {
				this._copyState = 'err';
			}
		} catch {
			this._copyState = 'err';
		}
		this.requestUpdate();
		if (this._copyState === 'ok') {
			setTimeout(() => { this._copyState = 'idle'; this.requestUpdate(); }, 2500);
		}
	}

	private async _downloadPreviewPdf(): Promise<void> {
		const preview = this._personPreview;
		if (!preview || this._pdfLoading) return;
		this._pdfLoading = true;
		this.requestUpdate();
		await this.updateComplete;
		try {
			// Renderiza el .container VIVO del iframe del preview (layout ya calculado por el
			// navegador = idéntico a lo que se ve). Se oculta el botón de confirmación (no aplica
			// para el PDF). Usar el elemento vivo evita que html2canvas re-calcule márgenes y
			// encime textos (pasaba al re-parsear el HTML en un holder suelto).
			const iframe = this.renderRoot.querySelector('iframe') as HTMLIFrameElement | null;
			const doc = iframe?.contentDocument;
			const container = doc?.querySelector('.container') as HTMLElement | null;
			if (!container) throw new Error('preview no disponible');
			const confirmWrap = (container.querySelector('.btn-confirm')?.parentElement) as HTMLElement | null;
			const prevDisplay = confirmWrap?.style.display;
			if (confirmWrap) confirmWrap.style.display = 'none';
			let canvas: HTMLCanvasElement;
			try {
				canvas = await html2canvas(container, {scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false, windowWidth: doc!.documentElement.scrollWidth, windowHeight: doc!.documentElement.scrollHeight});
			} finally {
				if (confirmWrap) confirmWrap.style.display = prevDisplay || '';
			}
			const imgW = canvas.width;
			const imgH = canvas.height;
			const pdf = new jsPDF({unit: 'px', format: [imgW, imgH], orientation: imgW >= imgH ? 'landscape' : 'portrait', hotfixes: ['px_scaling']});
			pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, imgH);
			const safe = (preview.row.personaName || 'invitacion').replace(/[^\w\s.-]/g, '').trim().slice(0, 40) || 'invitacion';
			pdf.save(`Invitacion - ${safe}.pdf`);
		} catch (e) {
			this._msg = {type: 'err', text: 'No se pudo generar el PDF: ' + (e instanceof Error ? e.message : String(e))};
		}
		this._pdfLoading = false;
		this.requestUpdate();
	}

	private async _runChunk(ids: string[]): Promise<StepChunkResult[]> {
		const res = await clamBackend.sendBatch(this._selectedId, {
			invitationIds: ids,
			templateId: this._selectedTemplateId || undefined,
			concurrency: 5,
			onlyUnsent: false,
		});
		if (!res.ok || !res.data) {
			// fallo del chunk entero (red/auth): marcar todos como err
			const error = res.statusCode === 401 ? 'Sesión expirada' : (res.error || 'Error de red');
			return ids.map((id) => ({id, ok: false, error}));
		}
		const data = res.data as SendResult;
		return ids.map((id) => {
			const d = data.details.find((x) => x.invitationId === id);
			return d ? {id, ok: d.success, error: d.error} : {id, ok: false, error: 'Sin respuesta del servidor'};
		});
	}

	render() {
		const enviados = this._invitations.filter((i) => i.enviado).length;
		const confirmados = this._invitations.filter((i) => i.confirmado).length;
		const evento = this._eventos.find((e) => e.id === this._selectedId);
		const mod = evento?.modalidad || '';
		const modClass = mod === 'presencial' ? 'mod-presencial' : mod === 'virtual' ? 'mod-virtual' : 'mod-hibrido';
		const modLabel = mod === 'presencial' ? 'Presencial' : mod === 'virtual' ? 'Virtual' : mod === 'hibrido' ? 'Híbrido' : '';
		const selectedTpl = this._templates.find((t) => t.id === this._selectedTemplateId);
		const tplHint = selectedTpl
			? `Se enviará con la plantilla: ${selectedTpl.name}`
			: 'Se enviará con la plantilla predeterminada (puedes editarla en “Plantillas de Correo”).';
		const busy = this._action !== null;
		return html`
			<h2>Invitaciones y Correos</h2>
			${modLabel ? html`<div style="margin-bottom:8px"><span class="mod-badge ${modClass}">${modLabel}</span></div>` : ''}
			<p class="desc">Genera códigos QR por persona, elige la plantilla del correo y envía las invitaciones con SendGrid. Rastrea confirmaciones de recepción.</p>
			<div class="toolbar">
				<select @change=${this._onSelect} .value=${this._selectedId} ?disabled=${busy}>
					${this._eventos.map((e) => html`<option value=${e.id}>${e.eventName}</option>`)}
				</select>
				<button class="btn btn-primary" @click=${() => this._generate()} ?disabled=${busy || !this._selectedId}>
					${this._action === 'generate' ? html`<span class="spinner"></span>` : ''} Generar QR
				</button>
				<button class="btn btn-send" @click=${() => this._sendBatch()} ?disabled=${busy || this._invitations.length === 0}>
					${this._action === 'send' ? html`<span class="spinner"></span>` : ''} Enviar en lotes
				</button>
			<button class="btn btn-preview" @click=${() => this._preview()} ?disabled=${busy || !this._selectedId}>
				${this._action === 'preview' ? html`<span class="spinner"></span>` : ''} Vista previa
			</button>
			<button class="btn btn-secondary" @click=${() => this._export()} ?disabled=${busy || this._exporting || this._invitations.length === 0}>
				${this._exporting ? html`<span class="spinner"></span>` : html`${iconDownload()} Exportar`}
			</button>
		</div>
			<div class="tpl-bar">
				<label for="tpl-select">Plantilla:</label>
				<select id="tpl-select" @change=${this._onSelectTemplate} .value=${this._selectedTemplateId} ?disabled=${busy}>
					<option value="">Predeterminada</option>
					${this._templates.map((t) => html`<option value=${t.id}>${t.name}</option>`)}
				</select>
				<button class="tpl-link" @click=${() => this._openTemplateEditor()} ?disabled=${!this._selectedId}>
					${iconPencil()} Diseñar plantilla de este evento
				</button>
				<div class="tpl-hint">${tplHint}</div>
			</div>
			${this._invitations.length > 0 ? html`
				<div class="stats">
					<div class="stat"><div class="stat-num">${this._invitations.length}</div><div class="stat-label">Total</div></div>
					<div class="stat stat-env"><div class="stat-num">${enviados}</div><div class="stat-label">Enviados</div></div>
					<div class="stat stat-conf"><div class="stat-num">${confirmados}</div><div class="stat-label">Confirmados</div></div>
				</div>
			` : ''}
			${this._msg ? html`<div class="msg msg-${this._msg.type}">${this._msg.text}</div>` : ''}
			${this._invitations.length === 0
				? html`<div class="empty">${this._loading || this._loadingEvento ? 'Cargando...' : 'No hay invitaciones. Haz clic en "Generar QR" para crearlas desde los inscritos.'}</div>`
				: html`
					<div class="inv-table">
						<div class="table-scroll">
							<table>
								<thead>
									<tr>
										<th>Persona</th>
										<th>Correo</th>
										<th>Empresa</th>
										<th style="text-align:center">QR</th>
										<th style="text-align:center">Enviado</th>
										<th style="text-align:center">Confirmado</th>
										<th>Envíos</th>
										<th style="text-align:center">Acción</th>
									</tr>
								</thead>
								<tbody>
									${this._invitations.map((inv) => html`
										<tr>
											<td><strong>${inv.personaName || '—'}</strong></td>
											<td style="color:var(--clam-text-muted);font-size:12px">${inv.personaEmail || '—'}</td>
											<td style="color:var(--clam-text-muted);font-size:12px">${inv.personaEmpresa || '—'}</td>
											<td style="text-align:center">${inv.qrDataUrl ? html`<img class="qr-preview" src=${inv.qrDataUrl} alt="QR" />` : '—'}</td>
									<td style="text-align:center">
										<span class="check ${inv.enviado ? 'check-yes' : 'check-no'}">${inv.enviado ? iconCheck() : iconX()}</span>
									</td>
									<td style="text-align:center">
										<span class="check ${inv.confirmado ? 'check-yes' : 'check-no'}">${inv.confirmado ? iconCheck() : iconX()}</span>
									</td>
											<td>
												<span class="badge ${inv.enviosCount > 0 ? 'badge-env' : 'badge-pend'}">${inv.enviosCount}</span>
											</td>
										<td style="text-align:center">
											<div class="row-actions">
												<button class="btn-icon" title="Vista previa de la invitación" @click=${() => this._openPersonPreview(inv)} ?disabled=${busy || this._loadingPreview === inv.id}>
													${this._loadingPreview === inv.id ? html`<span class="spinner"></span>` : html`${iconCopy()} Ver`}
												</button>
												<button class="btn-icon" @click=${() => this._sendOne(inv.id)} ?disabled=${busy || this._sendingRow === inv.id}>
													${this._sendingRow === inv.id ? '...' : html`${iconMail()} Enviar`}
												</button>
											</div>
										</td>
									</tr>
									`)}
								</tbody>
							</table>
						</div>
					</div>
					<div class="inv-cards">
						${this._invitations.map((inv) => html`
							<div class="inv-card">
								${inv.qrDataUrl ? html`<img class="inv-qr" src=${inv.qrDataUrl} alt="QR" />` : html`<div class="inv-qr"></div>`}
								<div class="inv-info">
									<div class="inv-name">${inv.personaName || '—'}</div>
									<div class="inv-meta">${inv.personaEmail || '—'}${inv.personaEmpresa ? html` · ${inv.personaEmpresa}` : ''}</div>
									<div class="inv-tags">
										<span class="badge ${inv.enviado ? 'badge-env' : 'badge-pend'}">${inv.enviado ? 'Enviado' : 'Pendiente'}</span>
										<span class="badge ${inv.confirmado ? 'badge-env' : 'badge-pend'}">${inv.confirmado ? 'Confirmado' : 'Sin confirmar'}</span>
										<span class="badge badge-pend">${inv.enviosCount} envíos</span>
									</div>
								</div>
							<div class="inv-action">
								<button class="btn-icon" title="Vista previa" @click=${() => this._openPersonPreview(inv)} ?disabled=${busy || this._loadingPreview === inv.id}>
									${this._loadingPreview === inv.id ? html`<span class="spinner"></span>` : html`${iconCopy()} Ver`}
								</button>
								<button class="btn-icon" @click=${() => this._sendOne(inv.id)} ?disabled=${busy || this._sendingRow === inv.id}>
									${this._sendingRow === inv.id ? '...' : html`${iconMail()} Enviar`}
								</button>
							</div>
							</div>
						`)}
					</div>
				`}
			${this._previewHtml ? html`
				<div class="preview-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closePreview(); }}>
					<div class="preview-modal">
						<div class="preview-header">
							<h3>Vista previa del email</h3>
							<button class="preview-close" @click=${() => this._closePreview()}>&times;</button>
						</div>
						<div class="preview-body">
							<iframe
								.srcdoc=${this._previewHtml}
								@load=${(e: Event) => {
									const iframe = e.target as HTMLIFrameElement;
									try {
										iframe.style.height = iframe.contentDocument?.body?.scrollHeight + 'px';
									} catch { /* cross-origin fallback */ }
								}}
							></iframe>
						</div>
					</div>
				</div>
			` : ''}
			${this._personPreview ? html`
				<div class="preview-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closePersonPreview(); }}>
					<div class="preview-modal">
						<div class="preview-header">
							<h3>${this._personPreview.row.personaName || 'Invitado'} — Vista previa</h3>
							<button class="preview-close" @click=${() => this._closePersonPreview()}>&times;</button>
						</div>
						<div class="preview-body">
							<iframe
								.srcdoc=${this._personPreview.html}
								@load=${(e: Event) => {
									const iframe = e.target as HTMLIFrameElement;
									try {
										iframe.style.height = iframe.contentDocument?.body?.scrollHeight + 'px';
									} catch { /* cross-origin fallback */ }
								}}
							></iframe>
						</div>
						<div class="preview-footer">
							<div class="preview-subject">Asunto: <strong>${this._personPreview.subject}</strong></div>
							<div class="preview-actions">
								<button class="btn btn-secondary" @click=${() => this._copyPreviewHtml()}>
									${iconCopy()} ${this._copyState === 'ok' ? '¡Copiado!' : this._copyState === 'err' ? 'Falló' : 'Copiar'}
								</button>
								<button class="btn btn-primary" @click=${() => this._downloadPreviewPdf()} ?disabled=${this._pdfLoading}>
									${this._pdfLoading ? html`<span class="spinner"></span>` : html`${iconDownload()} Descargar PDF`}
								</button>
							</div>
						</div>
					</div>
				</div>
			` : ''}
			${this._confirmBatch ? html`
				<div class="confirm-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._cancelConfirmSend(); }}>
					<div class="confirm-modal" role="dialog" aria-modal="true">
						<div class="modal__header">
							<span class="ic">${iconWarning()}</span>
							<h3>Confirmar envío masivo</h3>
						</div>
						<div class="modal__body">
							Vas a enviar <strong>${this._confirmBatch.length}</strong> correo${this._confirmBatch.length === 1 ? '' : 's'} de invitación
							${this._selectedTemplateId ? html` con la plantilla <strong>${this._templates.find((t) => t.id === this._selectedTemplateId)?.name || 'seleccionada'}</strong>` : ' con la plantilla predeterminada'}.
							<br /><br />
							Podés cancelar a mitad del envío; los ya enviados quedan registrados.
						</div>
						<div class="modal__footer">
							<button class="btn btn-secondary" @click=${() => this._cancelConfirmSend()}>Cancelar</button>
							<button class="btn btn-send" @click=${() => this._confirmSendBatch()}>Enviar ${this._confirmBatch.length} correo${this._confirmBatch.length === 1 ? '' : 's'}</button>
						</div>
					</div>
				</div>
			` : ''}
			<clam-step-progress
				.open=${!!this._progressItems}
				.items=${this._progressItems || []}
				.onChunk=${(ids: string[]) => this._runChunk(ids)}
				chunkSize="25"
				title="Enviando correos"
				@clam-step-progress-done=${this._onProgressDone}
				@clam-step-progress-close=${this._onProgressClose}
			></clam-step-progress>
		`;
	}
}

customElements.define('clam-qr-manager', ClamQrManager);
